/**
 * Carrousel « Ce que permet votre don » : un palier à la fois,
 * boucle infinie (Karim 2026-10-12). Partagé par les pages de paiement
 * (`DonateImpactOptions`, plus de 2 paliers), la carte de don (ordinateur)
 * et le panneau de don (téléphone).
 *
 * Un RAIL de 3 paliers côte à côte (précédent, actuel, suivant) : en glissant
 * on voit arriver le voisin, jamais du vide. Le rail coulisse jusqu'au voisin
 * puis se recale au centre sur le nouvel index.
 */
import { computed, ref, type Ref } from "vue";
import type { ImpactOption } from "~/components/donate/DonateImpactOptions.vue";

export function useCarrouselPaliers(paliers: Ref<ImpactOption[]>) {
  const indexPalier = ref(0);
  const nbPaliers = computed(() => paliers.value.length);
  /**
   * Le rail : précédent, actuel, suivant. Clé = le PALIER (son rang), pas la
   * place dans le rail : en fin de glissement, chaque palier garde son élément
   * et ne fait que changer de place. Avant, les 3 places changeaient de
   * contenu → images rechargées et coche ré-animée = clignotement
   * (Karim 2026-10-13). Un palier présent deux fois (2 paliers) : « 1-2 ».
   */
  const rail = computed(() => {
    const n = nbPaliers.value;
    const vus = new Map<number, number>();
    return [-1, 0, 1]
      .map((d) => {
        const i = (((indexPalier.value + d) % n) + n) % n;
        const fois = (vus.get(i) ?? 0) + 1;
        vus.set(i, fois);
        return { cle: fois === 1 ? String(i) : `${i}-${fois}`, palier: paliers.value[i] };
      })
      .filter((x): x is { cle: string; palier: ImpactOption } => !!x.palier);
  });
  /** -1 : le rail part vers le précédent, +1 : vers le suivant, 0 : au repos. */
  const cible = ref(0);
  const anime = ref(false);
  const decalageGlisse = ref(0);
  /** Espace entre deux paliers du rail (px), compté dans le décalage. */
  const ECART_PALIERS = 12;
  const styleRail = computed(() => {
    const pas = -1 - cible.value; // -1 = au centre, 0 = précédent, -2 = suivant
    return {
      gap: `${ECART_PALIERS}px`,
      transform: `translateX(calc(${pas * 100}% + ${pas * ECART_PALIERS + decalageGlisse.value}px))`,
    };
  });
  function changerPalier(pas: number) {
    if (nbPaliers.value < 2 || anime.value) return;
    anime.value = true;
    cible.value = pas;
  }
  /** Palier demandé pendant un coulissement : on y va juste après. */
  let enAttente: number | null = null;
  /** Fin du coulissement : on change d'index et on recale le rail au centre, sans animation. */
  function finAnimation() {
    if (cible.value !== 0) {
      const n = nbPaliers.value;
      indexPalier.value = (indexPalier.value + cible.value + n) % n;
    }
    anime.value = false;
    cible.value = 0;
    if (enAttente !== null) {
      const i = enAttente;
      enAttente = null;
      allerA(i);
    }
  }
  /**
   * Aller au palier `i` : en glissant s'il est voisin, directement sinon (ou
   * si `animer` est faux, ex. à l'ouverture de la page).
   */
  function allerA(i: number, animer = true) {
    const n = nbPaliers.value;
    if (n < 2 || i < 0 || i >= n) return;
    if (anime.value) {
      enAttente = i;
      return;
    }
    if (i === indexPalier.value) return;
    if (animer && i === (indexPalier.value + 1) % n) changerPalier(1);
    else if (animer && i === (indexPalier.value - 1 + n) % n) changerPalier(-1);
    else indexPalier.value = i;
  }

  // Glisser : souris (cliquer-glisser), doigt, ou pavé tactile (deux doigts).
  const enGlisse = ref(false);
  let departX: number | null = null;
  let aGlisse = false;
  function debutGlisse(e: PointerEvent) {
    if (nbPaliers.value < 2 || anime.value) return;
    departX = e.clientX;
    aGlisse = false;
  }
  function pendantGlisse(e: PointerEvent) {
    if (departX === null) return;
    const dx = e.clientX - departX;
    if (!enGlisse.value && Math.abs(dx) > 6) {
      enGlisse.value = true;
      aGlisse = true;
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    }
    if (enGlisse.value) decalageGlisse.value = dx;
  }
  function finGlisse() {
    if (departX === null) return;
    departX = null;
    if (!enGlisse.value) return;
    enGlisse.value = false;
    const dx = decalageGlisse.value;
    // Le rail repart de là où le doigt l'a laissé : vers le voisin (> 50 px)
    // ou retour au centre.
    anime.value = true;
    cible.value = Math.abs(dx) > 50 ? (dx < 0 ? 1 : -1) : 0;
    decalageGlisse.value = 0;
  }
  /** Un glisser n'est pas un clic : il ne choisit pas le montant du palier. */
  function bloquerClicApresGlisse(e: MouseEvent) {
    if (aGlisse) {
      e.stopPropagation();
      e.preventDefault();
      aGlisse = false;
    }
  }
  // Pavé tactile : glissement horizontal à deux doigts.
  let cumulRoue = 0;
  function roue(e: WheelEvent) {
    if (nbPaliers.value < 2 || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    e.preventDefault();
    if (anime.value) return;
    cumulRoue += e.deltaX;
    if (Math.abs(cumulRoue) > 40) {
      changerPalier(cumulRoue > 0 ? 1 : -1);
      cumulRoue = 0;
    }
  }

  return {
    indexPalier,
    nbPaliers,
    allerA,
    rail,
    anime,
    enGlisse,
    styleRail,
    changerPalier,
    finAnimation,
    debutGlisse,
    pendantGlisse,
    finGlisse,
    bloquerClicApresGlisse,
    roue,
  };
}
